import { useEffect } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { MapPin, ReceiptText, MessageSquare, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Badge, Rating, Verified, Feature } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { services as mockServices, peso } from "../data/mock";
import ReportButton from "../components/ReportButton";
import { setMeta, resetMeta } from "../lib/seo";
import { SaveButton } from "../components/SaveButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { useAuth } from "../lib/auth";
import { messagesHref } from "../lib/actions";

export default function ServiceDetail() {
  const [, params] = useRoute("/s/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const { item: raw, state } = useListing("services", id, mockServices);

  useEffect(() => {
    if (raw) setMeta({
      title: `${raw.title} — KontrataPH`,
      description: [raw.by ? `by ${raw.by}` : "", raw.city || "", raw.fromPrice ? `from ₱${Number(raw.fromPrice).toLocaleString("en-PH")}` : ""].filter(Boolean).join(" · ") || undefined,
    });
    return () => resetMeta();
  }, [raw]);

  if (state === "loading") return <DetailLoading label="Loading service…" />;
  if (state === "notfound" || !raw) return <DetailNotFound title="Service not found" backHref="/marketplace?tab=Services" backLabel="Browse Services" />;

  const s = { ...raw, from: raw.fromPrice ?? raw.from ?? 0 };
  const tags: string[] = Array.isArray(s.tags) ? s.tags : [];

  // CTA logic shared by the side card and the mobile sticky bar
  const isSelf = !!user && s.ownerUserId === user.id;
  const quoteHref = messagesHref(s.ownerUserId, { type: "service", id, name: s.title, intent: "quote" });
  const msgHref = messagesHref(s.ownerUserId, { type: "service", id, name: s.title });
  const disabledReason = isSelf ? "This is your listing" : "Not available on this sample listing";
  const go = (target: string | null) => () => { if (!user) return navigate("/login"); if (target) navigate(target); };
  const ctaDisabled = !!user && (isSelf || !msgHref);

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="text-sm text-mut mb-4"><Link href="/marketplace?tab=Services" className="hover:text-navy">Services</Link> / {s.title}</div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <div className="space-y-4">
          <div className="card p-6">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-extrabold text-navy">{s.title}</h1>
              {s.verified && <Verified label={s.company ? "Verified Company" : "Verified"} />}
            </div>
            <p className="text-mut mt-1">by {s.by}{s.company ? " (Company)" : ""}</p>
            <p className="text-sm text-mut flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{s.city}</p>
            <div className="mt-2"><Rating value={s.rating} count={s.reviews} /></div>
            <div className="flex flex-wrap gap-2 mt-4">{tags.map((t) => <Badge key={t}>{t}</Badge>)}</div>
          </div>

          <div className="card p-5"><h3 className="font-bold text-navy mb-2">About this service</h3>
            <p className="text-sm text-mut">{s.title} offered by {s.by}, serving {s.city}. Request a quotation for a scope and price tailored to your needs.</p>
          </div>

          <div className="card p-5"><h3 className="font-bold text-navy mb-3">What's included</h3>
            {tags.length ? <ul className="grid sm:grid-cols-2 gap-2">{tags.map((t) => <Feature key={t}>{t}</Feature>)}</ul> : <p className="text-sm text-mut">Scope discussed on request.</p>}
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-5">
            <div className="text-xs text-mut">Starting at</div>
            <div className="text-2xl font-extrabold text-navy">{peso(s.from)}</div>
            <div className="flex flex-col gap-2 mt-4">
              <button onClick={go(quoteHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
                className="btn-red w-full disabled:opacity-50 disabled:cursor-not-allowed"><ReceiptText className="w-4 h-4" />Request Quote</button>
              <button onClick={go(msgHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
                className="btn-navy w-full disabled:opacity-50 disabled:cursor-not-allowed"><MessageSquare className="w-4 h-4" />Message Provider</button>
              {user
                ? <SaveButton kind="service" refId={id} label="Save" className="w-full justify-center" />
                : <button onClick={() => navigate("/login")} className="btn-outline w-full">Save</button>}
            </div>
            <p className="text-[11px] text-mut flex items-center gap-1 justify-center mt-3"><ShieldCheck className="w-3 h-3" />Escrow &amp; milestone protection on hire</p>
            <ReportButton targetType="service" targetId={s.id} targetUserId={s.ownerUserId} className="mt-3" />
          </div>
          <div className="card p-5"><h3 className="font-bold text-navy mb-3">Why book here</h3>
            <ul className="space-y-2">
              <li className="text-sm text-mut flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" />Verified provider identity</li>
              <li className="text-sm text-mut flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" />Transparent reviews</li>
              <li className="text-sm text-mut flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green" />Secure payments</li>
            </ul>
          </div>
        </div>
      </div>

      <MobileActionBar>
        <div className="shrink-0">
          <div className="text-[11px] text-mut leading-none">Starting at</div>
          <div className="text-lg font-extrabold text-navy leading-tight">{peso(s.from)}</div>
        </div>
        <button onClick={go(quoteHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
          className="btn-red flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><ReceiptText className="w-4 h-4" />Request Quote</button>
      </MobileActionBar>
    </div>
  );
}
